import { useQueryClient } from '@tanstack/react-query';

import { useMutation } from '@hooks/reactQuery';

import {
	deleteKpiAlertForRanById,
	deleteKpiAlerts as deleteManyKpiAlerts,
	deleteManyKpiAlertsForRan,
} from '@api/kpiAlerts';

import { TOnSuccessOnError } from '@interfaces/common/handlers';

import { EVendorsValues, TVendorsValues } from '@enums/common';

import { DELETE_KPI_ALERTS, KPI_ALERTS, KPI_ALERTS_ORAN } from '@constants/queryKeys';

export interface IUseDeleteKpiAlertsResponse {
	deleteKpiAlerts: (ids: string[]) => void;
	deleteKpiAlertById: (id: string) => void;
	isLoading: boolean;
}

export function useDeleteKpiAlerts({
	onSuccess,
	onError,
	vendor,
}: Partial<TOnSuccessOnError> & { vendor?: TVendorsValues }): IUseDeleteKpiAlertsResponse {
	const queryClient = useQueryClient();

	const deleteManyFn = vendor === EVendorsValues.oran
		? deleteManyKpiAlertsForRan
		: deleteManyKpiAlerts;

	const deleteByIdFn = vendor === EVendorsValues.oran
		? deleteKpiAlertForRanById
		: () => null;

	const {
		mutate: deleteById,
		isLoading: isDeletingById,
	} = useMutation<any, void>({
		mutationKey: [DELETE_KPI_ALERTS],
		mutationFn: deleteByIdFn,
		onSuccess: () => {
			void queryClient.invalidateQueries([KPI_ALERTS]);
			void queryClient.invalidateQueries([KPI_ALERTS_ORAN]);
		},
		onError: () => {
			void queryClient.invalidateQueries([KPI_ALERTS]);
			void queryClient.invalidateQueries([KPI_ALERTS_ORAN]);
		},
	});

	const {
		mutate: deleteMany,
		isLoading: isDeleting,
	} = useMutation<any, void>({
		mutationKey: [DELETE_KPI_ALERTS],
		mutationFn: deleteManyFn,
		onSuccess: () => {
			void queryClient.invalidateQueries([KPI_ALERTS]);
			void queryClient.invalidateQueries([KPI_ALERTS_ORAN]);

			if (onSuccess) {
				onSuccess();
			}
		},
		onError: () => {
			void queryClient.invalidateQueries([KPI_ALERTS]);
			void queryClient.invalidateQueries([KPI_ALERTS_ORAN]);

			if (onError) {
				onError();
			}
		},
	});

	return {
		deleteKpiAlerts: deleteMany,
		deleteKpiAlertById: deleteById,
		isLoading: isDeletingById || isDeleting,
	};
}
